import { Label } from '@connected/form-fields/components/label';
import { NumberField } from '@connected/form-fields/components/number';
import { Button } from '@connected/ui/components/button';
import { Popout } from '@connected/ui/components/popout';
import { useForm } from '@tanstack/react-form';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

type CopyButtonProps = {
    deviceId: string;
    onCopy: (deviceId: string, count: number) => Promise<void>;
};

export function CopyButton({ deviceId, onCopy }: CopyButtonProps) {
    const { t: c } = useTranslation('common');
    const [open, setOpen] = useState(false);

    const form = useForm({
        defaultValues: {
            copyCount: 1,
        },
        validators: {
            onSubmit: z.object({
                copyCount: z.number().min(1).max(100),
            }),
        },
        onSubmit: async ({ value }) => {
            await onCopy(deviceId, value.copyCount);
            setOpen(false);
        },
    });

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            form.reset();
        }
        setOpen(open);
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        form.handleSubmit();
    };

    return (
        <Popout open={open} onOpenChange={handleOpenChange}>
            <Popout.Trigger className="flex w-full cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground">
                {c('actions.file.copy')}
            </Popout.Trigger>
            <Popout.Content>
                <Popout.Header>
                    <Popout.Title className="text-sm font-medium">Aantal kopieën</Popout.Title>
                    <Popout.Description className="sr-only">Kies het aantal kopieën die je wilt maken</Popout.Description>
                </Popout.Header>
                <form onSubmit={handleSubmit}>
                    <Popout.Body>
                        <form.Field name="copyCount">
                            {field => (
                                <Label label="Aantal kopieën" className="w-full">
                                    <NumberField
                                        name="copy-count"
                                        value={field.state.value}
                                        onChange={value => field.handleChange(Math.max(1, Math.min(100, value ?? 1)))}
                                        min={1}
                                        max={100}
                                        clamp
                                        className="h-8"
                                    />
                                </Label>
                            )}
                        </form.Field>
                    </Popout.Body>
                    <Popout.Footer>
                        <Button
                            variant="outline"
                            type="button"
                            onClick={() => setOpen(false)}
                        >
                            Annuleren
                        </Button>
                        <Button type="submit">
                            Bevestigen
                        </Button>
                    </Popout.Footer>
                </form>
            </Popout.Content>
        </Popout>
    );
}
